/**
 * I tre KPI in testa alla Panoramica.
 *
 * Rispondono in fila a tre domande: **dove siamo** · **cosa arriva** · **quando
 * ci vediamo**. Revisione del 21-08-2026 (Enrico).
 *
 * ⚠️ Il terzo era «Domande aperte» — il conteggio delle voci di bacheca in
 * attesa di risposta da Mister Pet. È stato tolto per due ragioni: è un elenco
 * di compiti puntato addosso a Enzo, mentre la regola su di lui è *solo
 * controllo, mai compiti*; e con 17 voci ferme a maggio lo accusava a torto.
 * Le domande aperte restano dietro la voce Bacheca in barra.
 */

import { CalendarDays, PackageCheck, TrendingUp } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { useAvanzamento } from "@/hooks/useAvanzamento";
import { useConsegnaProssima } from "@/hooks/useConsegnaProssima";
import { useIncontroProssimo } from "@/hooks/useIncontroProssimo";
import { cn } from "@/lib/utils";

export function KpiStrip() {
  return (
    <section className="grid gap-4 md:grid-cols-3">
      <KpiAvanzamento />
      <KpiProssimaConsegna />
      <KpiProssimoIncontro />
    </section>
  );
}

// ── Avanzamento progetto ────────────────────────────────────────────

function KpiAvanzamento() {
  const { data, isLoading } = useAvanzamento();

  return (
    <KpiCard icon={TrendingUp} label="Avanzamento progetto">
      {isLoading || !data ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-2 w-full" />
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-3xl font-semibold tracking-tight tabular-nums">
            {data.totale}%
          </p>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${data.totale}%` }}
              role="progressbar"
              aria-valuenow={data.totale}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          {/* Dice da dove esce il numero: la casella qui sotto lo scompone. */}
          <p className="text-xs text-muted-foreground">
            media di {data.n_moduli} moduli
          </p>
        </div>
      )}
    </KpiCard>
  );
}

// ── Prossima consegna ────────────────────────────────────────────────

function KpiProssimaConsegna() {
  const { data, isLoading } = useConsegnaProssima();

  return (
    <KpiCard icon={PackageCheck} label="Prossima consegna">
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground pt-1">
          Nessuna consegna in programma.
        </p>
      ) : (
        <div className="space-y-1">
          <p className="text-2xl font-semibold tracking-tight">
            {formatData(data.data)}
          </p>
          <p className="text-sm font-medium truncate" title={data.etichetta}>
            {data.etichetta}
          </p>
          <p className="text-xs text-muted-foreground">{giorniLabel(data.giorni_mancanti)}</p>
        </div>
      )}
    </KpiCard>
  );
}

// ── Prossimo incontro ────────────────────────────────────────────────

function KpiProssimoIncontro() {
  const { data, isLoading } = useIncontroProssimo();

  return (
    <KpiCard icon={CalendarDays} label="Prossimo incontro">
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground pt-1">Nessun incontro pianificato.</p>
      ) : (
        <div className="space-y-1">
          <p className="text-2xl font-semibold tracking-tight">{formatDataOra(data.data_ora)}</p>
          <p className="text-sm font-medium truncate" title={data.titolo}>
            {data.titolo}
          </p>
          {data.luogo && (
            <p className="text-xs text-muted-foreground truncate" title={data.luogo}>
              {data.luogo}
            </p>
          )}
        </div>
      )}
    </KpiCard>
  );
}

// ── Card guscio ──────────────────────────────────────────────────────

function KpiCard({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-lg border bg-card p-5 space-y-3")}>
      <div className="flex items-center gap-2">
        <Icon className="size-3.5 text-muted-foreground" />
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function giorniLabel(giorni: number): string {
  if (giorni <= 0) return "oggi";
  if (giorni === 1) return "fra un giorno";
  return `fra ${giorni} giorni`;
}

function formatDataOra(iso: string): string {
  const d = new Date(iso);
  const data = d.toLocaleDateString("it-IT", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const ora = d.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${data} · ${ora}`;
}
